import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  MinusIcon, 
  TrashIcon, 
  ShoppingCartIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const POS = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');

  // Debug: log cart changes
  React.useEffect(() => {
    console.log('Cart updated:', cart);
  }, [cart]);

  // جلب منتجات التاجر
  const { data: productsData, isLoading } = useQuery(
    ['vendor-products', searchTerm],
    () => api.get('/products/vendor/my-products', {
      params: { 
        search: searchTerm,
        status: 'active',
        limit: 50
      }
    }).then(res => {
      console.log('Products API response:', res.data);
      return res.data.data.products;
    }),
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('Error fetching products:', error);
        toast.error('حدث خطأ في جلب المنتجات');
      }
    }
  );

  // إنشاء طلب POS
  const createOrderMutation = useMutation(
    (orderData) => api.post('/orders/pos', orderData),
    {
      onSuccess: (response) => {
        toast.success('تم إنشاء الطلب بنجاح!');
        // إعادة تعيين النموذج
        setCart([]);
        setCustomerInfo({ name: '', phone: '', address: '' });
        setDiscount(0);
        setNotes('');
        queryClient.invalidateQueries('vendor-products');
        
        // طباعة الفاتورة (اختياري)
        if (window.confirm('هل تريد طباعة الفاتورة؟')) {
          printInvoice(response.data.data);
        }
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'حدث خطأ في إنشاء الطلب';
        toast.error(message);
      }
    }
  );

  // إضافة منتج للسلة
  const addToCart = (product) => {
    console.log('Adding product to cart:', product);
    
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      updateQuantity(product.id, existingItem.quantity + 1);
    } else {
      const newItem = {
        product_id: product.id,
        name: product.name,
        price: parseFloat(product.price),
        quantity: 1,
        available: product.quantity || 0
      };
      
      console.log('New cart item:', newItem);
      setCart([...cart, newItem]);
      toast.success(`تمت إضافة ${product.name}`);
    }
  };

  // تحديث الكمية
  const updateQuantity = (productId, newQuantity) => {
    const item = cart.find(i => i.product_id === productId);
    
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    if (item && newQuantity > item.available) {
      toast.error(`الكمية المتوفرة: ${item.available}`);
      return;
    }

    setCart(cart.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  // حذف من السلة
  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  // حساب الإجماليات
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const taxRate = 0.05;
    const taxAmount = subtotal * taxRate;
    const discountAmount = parseFloat(discount) || 0;
    const total = subtotal + taxAmount - discountAmount;

    return {
      subtotal: subtotal.toFixed(2),
      tax: taxAmount.toFixed(2),
      discount: discountAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  // إنشاء الطلب
  const handleCreateOrder = () => {
    if (cart.length === 0) {
      toast.error('السلة فارغة');
      return;
    }

    if (!customerInfo.name || !customerInfo.phone) {
      toast.error('يرجى إدخال اسم العميل ورقم الهاتف');
      return;
    }

    const orderData = {
      customer_name: customerInfo.name,
      customer_phone: customerInfo.phone,
      customer_address: customerInfo.address,
      items: cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price
      })),
      payment_method: paymentMethod,
      payment_status: paymentStatus,
      discount_amount: parseFloat(discount) || 0,
      notes: notes
    };

    createOrderMutation.mutate(orderData);
  };

  // طباعة الفاتورة
  const printInvoice = (order) => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>فاتورة ${order.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #f2f2f2; }
            .total { font-weight: bold; font-size: 18px; }
          </style>
        </head>
        <body>
          <h1>فاتورة مبيعات</h1>
          <p><strong>رقم الطلب:</strong> ${order.order_number}</p>
          <p><strong>التاريخ:</strong> ${new Date().toLocaleDateString('ar-EG')}</p>
          <p><strong>العميل:</strong> ${order.shipping_address.name}</p>
          <p><strong>الهاتف:</strong> ${order.shipping_address.phone}</p>
          
          <table>
            <thead>
              <tr>
                <th>المنتج</th>
                <th>السعر</th>
                <th>الكمية</th>
                <th>الإجمالي</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map(item => `
                <tr>
                  <td>${item.product.name}</td>
                  <td>${item.price} جنيه</td>
                  <td>${item.quantity}</td>
                  <td>${item.total} جنيه</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <p><strong>المجموع الفرعي:</strong> ${order.subtotal} جنيه</p>
          <p><strong>الضريبة (5%):</strong> ${order.tax_amount} جنيه</p>
          ${order.discount_amount > 0 ? `<p><strong>الخصم:</strong> ${order.discount_amount} جنيه</p>` : ''}
          <p class="total"><strong>الإجمالي:</strong> ${order.total_amount} جنيه</p>
          
          <p style="text-align: center; margin-top: 40px;">شكراً لتعاملكم معنا</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const totals = calculateTotals();

  return (
    <VendorLayout>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">نقطة البيع (POS)</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* قائمة المنتجات */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* بحث */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="ابحث عن منتج..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                  <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-3" />
                </div>
              </div>

              {/* المنتجات */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : productsData && productsData.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
                  {productsData.map(product => (
                    <button
                      key={product.id}
                      onClick={() => {
                        console.log('Product clicked:', product);
                        addToCart(product);
                      }}
                      className="border rounded-lg p-3 hover:border-primary-500 hover:shadow-md transition text-right disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={product.quantity === 0}
                      type="button"
                    >
                      {product.images && product.images[0] && (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                      )}
                      <h3 className="font-semibold text-sm mb-1 line-clamp-2">{product.name}</h3>
                      <p className="text-primary-600 font-bold">{product.price} جنيه</p>
                      <p className="text-xs text-gray-500">المتوفر: {product.quantity}</p>
                      {product.quantity === 0 && (
                        <p className="text-xs text-red-500 mt-1">نفذت الكمية</p>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>لا توجد منتجات نشطة</p>
                  <p className="text-sm mt-2">قم بإضافة منتجات وتفعيلها أولاً</p>
                </div>
              )}
            </div>
          </div>

          {/* السلة والدفع */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-lg font-bold mb-4 flex items-center">
                <ShoppingCartIcon className="w-6 h-6 ml-2" />
                السلة ({cart.length})
              </h2>

              {/* عناصر السلة */}
              <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.product_id} className="flex items-center justify-between border-b pb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.price} جنيه</p>
                    </div>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="p-1 hover:bg-red-100 text-red-600 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* معلومات العميل */}
              <div className="space-y-3 mb-4 border-t pt-4">
                <input
                  type="text"
                  placeholder="اسم العميل *"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  className="form-input text-sm"
                />
                <input
                  type="tel"
                  placeholder="رقم الهاتف *"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  className="form-input text-sm"
                />
                <input
                  type="text"
                  placeholder="العنوان (اختياري)"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                  className="form-input text-sm"
                />
              </div>

              {/* طريقة الدفع */}
              <div className="space-y-3 mb-4 border-t pt-4">
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="cash">نقدي</option>
                  <option value="card">بطاقة</option>
                  <option value="bank_transfer">تحويل بنكي</option>
                </select>

                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="paid">مدفوع</option>
                  <option value="pending">معلق</option>
                </select>

                <input
                  type="number"
                  placeholder="الخصم (جنيه)"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="form-input text-sm"
                  min="0"
                  step="0.01"
                />

                <textarea
                  placeholder="ملاحظات (اختياري)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="form-input text-sm"
                  rows="2"
                />
              </div>

              {/* الإجماليات */}
              <div className="space-y-2 mb-4 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span>المجموع الفرعي:</span>
                  <span>{totals.subtotal} جنيه</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>الضريبة (5%):</span>
                  <span>{totals.tax} جنيه</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>الخصم:</span>
                    <span>-{totals.discount} جنيه</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>الإجمالي:</span>
                  <span className="text-primary-600">{totals.total} جنيه</span>
                </div>
              </div>

              {/* أزرار */}
              <div className="space-y-2">
                <button
                  onClick={handleCreateOrder}
                  disabled={cart.length === 0 || createOrderMutation.isLoading}
                  className="btn-primary w-full"
                >
                  {createOrderMutation.isLoading ? 'جاري الإنشاء...' : 'إنشاء الطلب'}
                </button>
                <button
                  onClick={() => setCart([])}
                  disabled={cart.length === 0}
                  className="btn-secondary w-full"
                >
                  مسح السلة
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default POS;
